import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  styled,
  Drawer,
  AppBar,
  IconButton,
  Card,
  Typography,
  Divider,
  Box,
  Link,
  Backdrop,
  Switch,
  Toolbar,
  AlertColor,
  useMediaQuery,
  Theme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ShareIcon from "@mui/icons-material/Share";
import {
  DoctorDocumentReadinessModal,
  Header,
  LogoutModal,
  Modal,
  SnackBar,
} from "@components";
import { useAuth, useDoctorContext, useSidebarMenuContext } from "@contexts";
import {
  useEditDoctor,
  useAppVersion,
  useDoctorDocumentReadiness,
} from "@hooks";
import {
  getDoctorTitle,
  checkIfBiometricAuthIsAvailable,
  onContactUsClick,
  shareDoctorLink,
  isSafari,
} from "@utils";
import {
  needHelpMessage,
  unsuscribeMessage,
  termsAndConditionsLink,
} from "../../../constants";
import MenuIcon from "@mui/icons-material/Menu";
import { MekiDocIcon } from "@icons";
import { DownloadPWAModal } from ".";

const StyledMekiDocIcon = styled(MekiDocIcon)(() => ({
  width: "85px",
  height: "16px",
  cursor: "pointer",
}));

const Version = styled(Typography)(({ theme }) => ({
  alignSelf: "center",
  position: "relative",
  bottom: "auto",
  marginTop: "auto",
  textTransform: "lowercase",
  color: theme.palette.text.disabled,
}));

const StyledSidebarMenuListItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isTitle",
})<{ onClick?: () => void; isTitle: string }>(
  ({ onClick, isTitle, theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: isTitle === "true" ? "15px 16px" : "16px 8px 16px 16px",
    cursor: onClick ? "pointer" : "default",
    "&:hover": {
      backgroundColor: onClick ? theme.palette.background.disabled : "inherit",
    },
  }),
);

const SideBarMenuTitle = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: "500",
  lineHeight: "27px",
  color: theme.palette.text.primary,
}));

const TextIconContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const StyledRightArrow = styled(KeyboardArrowRightIcon)(({ theme }) => ({
  transform: "scale(1.3)",
  color: theme.palette.icon.primary,
}));

interface SidebarMenuListItemProps {
  text: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  showArrow?: boolean;
  isTitle?: boolean;
  switchComponent?: React.ReactNode;
}

const SidebarMenuListItem: React.FC<SidebarMenuListItemProps> = ({
  text,
  icon,
  onClick,
  showArrow,
  isTitle,
  switchComponent,
}) => {
  return (
    <StyledSidebarMenuListItem
      onClick={onClick}
      isTitle={isTitle ? "true" : "false"}
    >
      {isTitle ? (
        <SideBarMenuTitle>{text}</SideBarMenuTitle>
      ) : (
        <TextIconContainer>
          {icon}
          <Typography color="text.black">{text}</Typography>
        </TextIconContainer>
      )}
      {showArrow && <StyledRightArrow />}
      {switchComponent}
    </StyledSidebarMenuListItem>
  );
};

const StyledBackDrop = styled(Backdrop)({
  zIndex: 9,
  backgroundColor: "rgba(0, 0, 0, 0.75)",
});

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  display: "flex",
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0, // Prevent AppBar from shrinking in flex layout
}));

const CloseIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.icon.primary,
  position: "absolute",
  right: "16px",
}));

const SidebarMenuBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px 8px",
  gap: "16px",
  backgroundColor: theme.palette.background.disabled,
  flex: 1,
  overflowY: "auto",
  minHeight: 0, // Allows flex child to shrink below content size
}));

const CenteredBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "16px 0px",
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  height: "24px",
  width: "24px",
  color: theme.palette.primary.main,
  background: theme.palette.primary.extralight,
}));

const StyledShareIcon = styled(ShareIcon)({
  width: "16px",
  height: "16px",
});

const StyledAddIcon = styled(AddIcon)({
  width: "20px",
  height: "20px",
});

const StyledIosShareIcon = styled(IosShareIcon)({
  width: "16px",
  height: "16px",
});

const ClickableText = styled(Typography)({
  cursor: "pointer",
  fontWeight: "500",
  "&:hover": {
    textDecoration: "underline",
  },
});

const Section = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  flexShrink: 0, // Prevent section from shrinking
});

const StyledCard = styled(Card)({
  flexShrink: 0, // Prevent card from shrinking
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: "600",
  lineHeight: "24px",
  color: theme.palette.text.primary,
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.primary,
  textDecoration: "none",
}));

export const SidebarMenu: React.FC = () => {
  const appVersion = useAppVersion();
  const navigate = useNavigate();
  const doctor = useDoctorContext();
  const location = useLocation();
  const { documentCreated = false } = location.state || {};
  const [showCopiedLinkModal, setShowCopiedLinkModal] = useState(false);
  const { ensureReady, modalProps } = useDoctorDocumentReadiness();
  const [snackBarState, setSnackBarState] = useState({
    open: false,
    type: "success" as AlertColor,
  });
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const showAddToHomeScreenBaseConditions = isSafari && !isMobile;

  const [showAddToHomeScreen, setShowAddToHomeScreen] = useState(
    showAddToHomeScreenBaseConditions &&
      doctor.showPwaAlert !== false &&
      documentCreated,
  );
  const { biometricSignUp } = useAuth();
  const { mutate: editDoctor } = useEditDoctor({
    code: doctor.code,
  });
  const { isSidebarMenuOpen, setIsSidebarMenuOpen } = useSidebarMenuContext();
  const [showLogOutModal, setShowLogOutModal] = useState(false);

  const [isBiometricAuthAvailable, setIsBiometricAuthAvailable] =
    useState(false);
  const [switchChecked, setSwitchChecked] = useState(doctor.biometricIsEnabled);

  const handleSwitchChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setSwitchChecked(checked);
      if (checked) {
        const signUpSuccess = await biometricSignUp(doctor);
        signUpSuccess
          ? editDoctor({ biometricIsEnabled: true })
          : setSwitchChecked(false);
      } else {
        editDoctor({
          biometricIsEnabled: false,
        });
      }
    },
    [biometricSignUp, doctor, editDoctor],
  );

  const handleClose = useCallback(() => {
    setIsSidebarMenuOpen(false);
  }, [setIsSidebarMenuOpen]);

  const onPatientLinkClick = useCallback(async () => {
    ensureReady(async () => {
      await shareDoctorLink({
        doctorCode: doctor.code,
        setShowCopiedLinkModal,
        setSnackBarState,
        isDesktop: !isMobile,
      });
    });
  }, [ensureReady, doctor.code, isMobile]);

  const onNewDocumentClick = useCallback(() => {
    ensureReady(() => {
      navigate(`/app/nuevo-documento`);
    });
  }, [ensureReady, navigate]);

  const handleClosePwaPrompt = useCallback(() => {
    setShowAddToHomeScreen(false);
    editDoctor({
      showPwaAlert: false,
    });
  }, [editDoctor]);

  useEffect(() => {
    async function checkBiometricAuth() {
      const isBiometricAuthAvailable = await checkIfBiometricAuthIsAvailable();
      setIsBiometricAuthAvailable(isBiometricAuthAvailable);
    }
    checkBiometricAuth();
  }, []);

  useEffect(() => {
    setSwitchChecked(doctor.biometricIsEnabled);
  }, [doctor.biometricIsEnabled]);

  useEffect(() => {
    function changeScrollBehavior() {
      if (!isMobile) {
        if (isSidebarMenuOpen) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "auto";
        }
      }
    }
    changeScrollBehavior();
  }, [isMobile, isSidebarMenuOpen]);

  return (
    <>
      <StyledBackDrop open={isSidebarMenuOpen} onClick={handleClose} />
      <Header
        left={
          <>
            <IconButton
              onClick={() => {
                setIsSidebarMenuOpen(true);
              }}
              sx={(theme) => ({ color: theme.palette.icon.primary })}
            >
              <MenuIcon />
            </IconButton>
            <StyledMekiDocIcon
              onClick={() => {
                navigate("/app/solicitudes");
              }}
            />
          </>
        }
      />
      <Drawer
        variant="persistent"
        anchor="left"
        open={isSidebarMenuOpen}
        elevation={0}
        PaperProps={{
          variant: "outlined",
          sx: {
            width: "340px",
          },
        }}
        transitionDuration={200}
      >
        <StyledAppBar>
          <Toolbar>
            <MekiDocIcon sx={{ width: "85px", height: "16px" }} />
            <CloseIconButton onClick={handleClose}>
              <CloseIcon />
            </CloseIconButton>
          </Toolbar>
        </StyledAppBar>
        <SidebarMenuBody>
          <StyledCard variant="outlined">
            <SidebarMenuListItem
              text={`${getDoctorTitle(doctor.gender).title} ${doctor.name} ${
                doctor.lastName
              }`}
              isTitle={true}
            />
            <Divider />
            <SidebarMenuListItem
              icon={<StyledIconButton children={<StyledShareIcon />} />}
              text={"Formulario para pacientes"}
              onClick={onPatientLinkClick}
            />
            <Divider />
            <SidebarMenuListItem
              icon={<StyledIconButton children={<StyledAddIcon />} />}
              text={"Nuevo documento"}
              onClick={() => {
                handleClose();
                onNewDocumentClick();
              }}
            />
            <Divider />
            {showAddToHomeScreenBaseConditions && (
              <>
                <SidebarMenuListItem
                  icon={<StyledIconButton children={<StyledIosShareIcon />} />}
                  text={"Descargar app"}
                  onClick={() => {
                    handleClose();
                    setShowAddToHomeScreen(true);
                  }}
                />
                <Divider />
              </>
            )}
            <CenteredBox>
              <ClickableText
                variant="body1"
                color="primary"
                onClick={() => setShowLogOutModal(true)}
              >
                Cerrar sesión
              </ClickableText>
              <LogoutModal
                showLogOutModal={showLogOutModal}
                setShowLogOutModal={setShowLogOutModal}
              />
            </CenteredBox>
          </StyledCard>
          <Section>
            <SectionTitle variant="h5">Mis datos</SectionTitle>
            <StyledCard variant="outlined">
              <SidebarMenuListItem
                text={"Datos personales"}
                onClick={() => {
                  navigate("/app/datos-personales");
                }}
                showArrow
              />
              <Divider />
              <SidebarMenuListItem
                text={"Dirección consulta"}
                onClick={() => {
                  navigate("/app/datos-personales/cambiar-direccion");
                }}
                showArrow
              />
              {isBiometricAuthAvailable && (
                <>
                  <Divider />
                  <SidebarMenuListItem
                    text={"Acceso con huella o Face ID"}
                    switchComponent={
                      <Switch
                        checked={switchChecked}
                        onChange={handleSwitchChange}
                      />
                    }
                  />
                </>
              )}
            </StyledCard>
          </Section>
          <Section>
            <SectionTitle variant="h5">Asistencia</SectionTitle>
            <StyledCard variant="outlined">
              <SidebarMenuListItem
                text={"Contáctanos"}
                onClick={() => {
                  onContactUsClick(needHelpMessage);
                }}
                showArrow
              />
              <Divider />
              <StyledLink
                target="_blank"
                rel="noopener"
                href={termsAndConditionsLink}
              >
                <SidebarMenuListItem
                  text={"Términos y condiciones"}
                  onClick={() => {}}
                  showArrow
                />
              </StyledLink>

              <Divider />
              <SidebarMenuListItem
                text={"Darme de baja"}
                onClick={() => {
                  onContactUsClick(unsuscribeMessage);
                }}
                showArrow
              />
            </StyledCard>
          </Section>
          <Version variant="caption">{appVersion}</Version>
        </SidebarMenuBody>
      </Drawer>

      <DoctorDocumentReadinessModal {...modalProps} />

      <Modal
        title="Link copiado"
        content="Pégalo en WhatsApp o en un correo para enviarlo al paciente."
        isOpen={showCopiedLinkModal}
        onClose={() => setShowCopiedLinkModal(false)}
        buttonProps={{
          buttonText: "Aceptar",
          onClick: () => {
            setShowCopiedLinkModal(false);
          },
        }}
      />

      <DownloadPWAModal
        isOpen={showAddToHomeScreen}
        onClose={handleClosePwaPrompt}
      />

      <SnackBar
        snackBarState={snackBarState}
        onClose={() => setSnackBarState({ ...snackBarState, open: false })}
        alerts={{
          success: "Link copiado, compártelo con tu paciente",
          error: "Ocurrió un error al copiar el link",
        }}
        style={{ margin: "0px" }}
      />
    </>
  );
};
